import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { structuredLogger } from "@/lib/logging";
import { useUserRole } from "./useUserRole";

export interface OrgBranding {
  name: string;
  logo_url: string | null;
  dark_logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  accent_color: string | null;
  tagline: string | null;
}

const defaultBranding: OrgBranding = {
  name: "",
  logo_url: null,
  dark_logo_url: null,
  primary_color: null,
  secondary_color: null,
  accent_color: null,
  tagline: null,
};

export const useOrgBranding = () => {
  const { organizationId } = useUserRole();
  const [branding, setBranding] = useState<OrgBranding>(defaultBranding);
  const [loading, setLoading] = useState(true);

  const loadBranding = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("name, logo_url, dark_logo_url, primary_color, secondary_color, accent_color, tagline")
        .eq("id", organizationId)
        .maybeSingle();

      if (error) {
        structuredLogger.error("Error loading org branding", { error: error.message });
      } else if (data) {
        setBranding({
          name: data.name || "",
          logo_url: data.logo_url,
          dark_logo_url: data.dark_logo_url,
          primary_color: data.primary_color,
          secondary_color: data.secondary_color,
          accent_color: data.accent_color,
          tagline: data.tagline,
        });
      }
    } catch (error) {
      structuredLogger.error("Error loading org branding", { error: error instanceof Error ? error.message : "Unknown" });
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  return { branding, loading, refetch: loadBranding };
};
